import React, { FC } from 'react';
import { FlagKind } from './platformStatsFeatureFlagTypes';

interface Props {
  id?: string;
  kind: FlagKind;
  value: string;
  onChange: (raw: string) => void;
  /** Variant names for `variant` flags. */
  variants?: string[];
  /** Adds an "(unset)" option for optional values such as rolloutValue. */
  allowEmpty?: boolean;
  disabled?: boolean;
}

/**
 * Kind-aware editor for a flag value. Works on the raw string form; callers
 * convert with `inputToValue` when submitting so validation errors surface
 * next to the form rather than on every keystroke.
 */
const FlagValueInput: FC<Props> = ({
  id,
  kind,
  value,
  onChange,
  variants = [],
  allowEmpty = false,
  disabled = false,
}) => {
  if (kind === 'boolean') {
    return (
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
      >
        {allowEmpty && <option value=''>(unset)</option>}
        <option value='true'>true</option>
        <option value='false'>false</option>
      </select>
    );
  }
  if (kind === 'variant') {
    return (
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
      >
        {allowEmpty && <option value=''>(unset)</option>}
        {variants.map((v) => (
          <option key={v} value={v}>
            {v}
          </option>
        ))}
      </select>
    );
  }
  if (kind === 'json') {
    return (
      <textarea
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={4}
        spellCheck={false}
        disabled={disabled}
        placeholder={allowEmpty ? '(unset)' : '{ … }'}
      />
    );
  }
  return (
    <input
      id={id}
      type={kind === 'number' ? 'number' : 'text'}
      step={kind === 'number' ? 'any' : undefined}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      placeholder={allowEmpty ? '(unset)' : undefined}
    />
  );
};

export default FlagValueInput;
